import type { Drink, Ingredient, Customer, CustomerType } from '@/types/game-3d';

export const INGREDIENTS: Record<string, Ingredient> = {
  coffee: { id: 'coffee', name_vi: 'Cà phê', name_en: 'Coffee', emoji: '☕' },
  milk: { id: 'milk', name_vi: 'Sữa', name_en: 'Milk', emoji: '🥛' },
  ice: { id: 'ice', name_vi: 'Đá', name_en: 'Ice', emoji: '🧊' },
  sugar: { id: 'sugar', name_vi: 'Đường', name_en: 'Sugar', emoji: '🍯' },
  tea: { id: 'tea', name_vi: 'Trà', name_en: 'Tea', emoji: '🍵' },
  lemon: { id: 'lemon', name_vi: 'Chanh', name_en: 'Lemon', emoji: '🍋' },
  peach: { id: 'peach', name_vi: 'Đào', name_en: 'Peach', emoji: '🍑' },
  salt: { id: 'salt', name_vi: 'Muối', name_en: 'Salt', emoji: '🧂' },
};

export const DRINKS: Record<string, Drink> = {
  ca_phe_den_da: {
    id: 'ca_phe_den_da',
    name_vi: 'Cà phê đen đá',
    name_en: 'Vietnamese iced black coffee',
    emoji: '☕',
    recipe: ['coffee', 'ice'],
  },
  ca_phe_sua_da: {
    id: 'ca_phe_sua_da',
    name_vi: 'Cà phê sữa đá',
    name_en: 'Vietnamese iced milk coffee',
    emoji: '🥤',
    recipe: ['coffee', 'milk', 'ice'],
  },
  bac_xiu: {
    id: 'bac_xiu',
    name_vi: 'Bạc xỉu',
    name_en: 'Bac xiu (milk with coffee)',
    emoji: '🥛',
    recipe: ['milk', 'coffee', 'ice'],
  },
  tra_da: {
    id: 'tra_da',
    name_vi: 'Trà đá',
    name_en: 'Iced tea',
    emoji: '🍵',
    recipe: ['tea', 'ice'],
  },
  tra_chanh: {
    id: 'tra_chanh',
    name_vi: 'Trà chanh',
    name_en: 'Lemon iced tea',
    emoji: '🍋',
    recipe: ['tea', 'lemon', 'ice'],
  },
  tra_dao: {
    id: 'tra_dao',
    name_vi: 'Trà đào',
    name_en: 'Peach iced tea',
    emoji: '🍑',
    recipe: ['tea', 'peach', 'ice'],
  },
  ca_phe_muoi: {
    id: 'ca_phe_muoi',
    name_vi: 'Cà phê muối',
    name_en: 'Salted coffee',
    emoji: '🧂',
    recipe: ['coffee', 'milk', 'salt'],
  },
};

// Patience theo type khách (V6.1 mục 12 + design spec 8.3)
export const CUSTOMER_PATIENCE: Record<CustomerType, number> = {
  student: 90,
  office: 75,
  shipper: 60,
  neighbor: 100,
  tiktoker: 80,
};

// Dialogue templates per customer type
const DIALOGUE_TEMPLATES: Record<CustomerType, { vi: string; en: string }[]> = {
  student: [
    { vi: 'Cho em một ly cà phê sữa đá ạ', en: 'One iced milk coffee, please' },
    { vi: 'Em muốn ly cà phê đen đá nha anh', en: 'Iced black coffee please' },
    { vi: 'Cho em bạc xỉu nha', en: 'A bac xiu please' },
  ],
  office: [
    { vi: 'Cho mình cà phê đen đá nhé', en: 'Iced black coffee, please' },
    { vi: 'Mình lấy ly bạc xỉu', en: "I'll have a bac xiu" },
    { vi: 'Cà phê sữa đá ít đường giúp mình', en: 'Iced milk coffee, less sugar' },
  ],
  shipper: [
    { vi: 'Anh ơi cho em đơn cà phê sữa đá, nhanh nhé!', en: 'Iced milk coffee, fast please!' },
    { vi: 'Cho em ly cà phê đen, em đang gấp', en: 'Black coffee, in a hurry' },
  ],
  neighbor: [
    { vi: 'Cho cô ly trà đá thôi cháu', en: 'Just an iced tea, please' },
    { vi: 'Bác lấy bạc xỉu', en: "I'll have a bac xiu" },
  ],
  tiktoker: [
    { vi: 'Em quay review thử ly cà phê muối của anh nha 📱', en: 'Reviewing your salted coffee 📱' },
    { vi: 'Cho em trà đào để chụp ảnh đẹp', en: 'Peach tea for the gram' },
  ],
};

const CUSTOMER_NAMES: Record<CustomerType, string> = {
  student: 'Sinh viên',
  office: 'Nhân viên VP',
  shipper: 'Shipper',
  neighbor: 'Cô chú',
  tiktoker: 'TikToker',
};

// Phase 1 drinks available per customer type
const CUSTOMER_DRINKS: Record<CustomerType, string[]> = {
  student: ['ca_phe_sua_da', 'ca_phe_den_da', 'bac_xiu'],
  office: ['ca_phe_den_da', 'bac_xiu', 'ca_phe_sua_da'],
  shipper: ['ca_phe_sua_da', 'ca_phe_den_da'],
  neighbor: ['tra_da', 'bac_xiu'],
  tiktoker: ['ca_phe_muoi', 'tra_dao'],
};

let customerCounter = 0;

export function generateCustomer(type: CustomerType): Customer {
  const dialogues = DIALOGUE_TEMPLATES[type];
  const dialogue = dialogues[Math.floor(Math.random() * dialogues.length)];
  const drinks = CUSTOMER_DRINKS[type];
  const drink = drinks[Math.floor(Math.random() * drinks.length)] as Customer['drink'];

  customerCounter++;
  return {
    id: `c_${Date.now()}_${customerCounter}`,
    type,
    name_vi: CUSTOMER_NAMES[type],
    patience_seconds: CUSTOMER_PATIENCE[type],
    dialogue_vi: dialogue.vi,
    dialogue_en: dialogue.en,
    drink,
  };
}

// Phase 1: only 3 customer types
export const PHASE_1_CUSTOMER_TYPES: CustomerType[] = ['student', 'office', 'shipper'];

export function generateRandomCustomer(): Customer {
  const type =
    PHASE_1_CUSTOMER_TYPES[Math.floor(Math.random() * PHASE_1_CUSTOMER_TYPES.length)];
  return generateCustomer(type);
}
